import { BLE_UUIDS, DeviceTelemetry, ServerConfig, ApnConfig } from './types';
import { MockBleDevice } from './mockBleDevice';

export class BleService {
  private device: BluetoothDevice | null = null;
  private server: BluetoothRemoteGATTServer | null = null;
  private txChar: BluetoothRemoteGATTCharacteristic | null = null;
  private rxChar: BluetoothRemoteGATTCharacteristic | null = null;

  public mockMode = false;
  public scanAll = false;
  public isRebootingAfterFlash = false;
  public isUserDisconnecting = false;
  public isRebootingSensor = false;
  public lastConnectedDevice: BluetoothDevice | null = null;
  public mockDevice: MockBleDevice = new MockBleDevice();
  private writeQueue: Promise<void> = Promise.resolve();

  private onNotifyListener?: (data: Uint8Array, text: string) => void;
  private onDisconnectListener?: () => void;
  private onLogListener?: (msg: string, type?: 'info' | 'warn' | 'error' | 'success') => void;
  private jsonListeners: Array<(obj: Record<string, unknown>, raw: string) => void> = [];

  private rxBuffer = '';

  constructor() {
    this.mockDevice.setNotifyCallback((bytes) => {
      const text = new TextDecoder().decode(bytes);
      this.handleIncomingChunk(bytes, text);
    });
    this.mockDevice.setDisconnectCallback(() => {
      if (this.onDisconnectListener) {
        this.onDisconnectListener();
      }
    });
  }

  public setCallbacks(opts: {
    onNotify?: (data: Uint8Array, text: string) => void;
    onDisconnect?: () => void;
    onLog?: (msg: string, type?: 'info' | 'warn' | 'error' | 'success') => void;
    onJson?: (obj: Record<string, unknown>, raw: string) => void;
  }) {
    if (opts.onNotify) this.onNotifyListener = opts.onNotify;
    if (opts.onDisconnect) this.onDisconnectListener = opts.onDisconnect;
    if (opts.onLog) this.onLogListener = opts.onLog;
    if (opts.onJson) this.jsonListeners.push(opts.onJson);
  }

  public addJsonListener(cb: (obj: Record<string, unknown>, raw: string) => void): () => void {
    this.jsonListeners.push(cb);
    return () => {
      this.jsonListeners = this.jsonListeners.filter((l) => l !== cb);
    };
  }

  private handleIncomingChunk(_bytes: Uint8Array, text: string) {
    this.rxBuffer += text;

    // Scan for complete JSON objects in rxBuffer
    while (true) {
      const startIdx = this.rxBuffer.indexOf('{');
      if (startIdx === -1) {
        // No JSON start found; check for non-JSON lines (e.g. AT responses, modem streaming)
        let lineEnd = this.rxBuffer.indexOf('\n');
        while (lineEnd !== -1) {
          const line = this.rxBuffer.slice(0, lineEnd).trim();
          this.rxBuffer = this.rxBuffer.slice(lineEnd + 1);
          if (line && this.onNotifyListener) {
            this.onNotifyListener(new TextEncoder().encode(line), line);
          }
          lineEnd = this.rxBuffer.indexOf('\n');
        }
        if (this.rxBuffer.length > 512) {
          const flush = this.rxBuffer.trim();
          if (flush && this.onNotifyListener) {
            this.onNotifyListener(new TextEncoder().encode(flush), flush);
          }
          this.rxBuffer = '';
        }
        break;
      }

      // Flush any leading non-JSON text before '{'
      if (startIdx > 0) {
        const leadText = this.rxBuffer.slice(0, startIdx).trim();
        if (leadText && this.onNotifyListener) {
          this.onNotifyListener(new TextEncoder().encode(leadText), leadText);
        }
        this.rxBuffer = this.rxBuffer.slice(startIdx);
      }

      // Now this.rxBuffer starts with '{'
      let braceCount = 0;
      let inString = false;
      let escape = false;
      let endIdx = -1;

      for (let i = 0; i < this.rxBuffer.length; i++) {
        const ch = this.rxBuffer[i];
        if (escape) {
          escape = false;
          continue;
        }
        if (ch === '\\') {
          escape = true;
          continue;
        }
        if (ch === '"') {
          inString = !inString;
          continue;
        }
        if (!inString) {
          if (ch === '{') braceCount++;
          else if (ch === '}') {
            braceCount--;
            if (braceCount === 0) {
              endIdx = i;
              break;
            }
          }
        }
      }

      if (endIdx !== -1) {
        const jsonStr = this.rxBuffer.slice(0, endIdx + 1);
        this.rxBuffer = this.rxBuffer.slice(endIdx + 1);
        try {
          const obj = JSON.parse(jsonStr) as Record<string, unknown>;
          for (const listener of this.jsonListeners) {
            listener(obj, jsonStr);
          }
          if (this.onNotifyListener) {
            this.onNotifyListener(new TextEncoder().encode(jsonStr), jsonStr);
          }
        } catch {
          // If parse fails, drop the leading '{' to avoid infinite loop
          this.rxBuffer = this.rxBuffer.slice(1);
        }
      } else {
        // Incomplete JSON object; wait for next BLE notification packet
        break;
      }
    }
  }

  public log(msg: string, type: 'info' | 'warn' | 'error' | 'success' = 'info') {
    if (this.onLogListener) this.onLogListener(msg, type);
  }

  public isSupported(): boolean {
    if (this.mockMode) return true;
    return typeof navigator !== 'undefined' && 'bluetooth' in navigator;
  }

  public async forgetKnownDevices(): Promise<number> {
    if (typeof navigator !== 'undefined' && 'bluetooth' in navigator && 'getDevices' in navigator.bluetooth) {
      try {
        const devices = await navigator.bluetooth.getDevices();
        let forgotten = 0;
        for (const dev of devices) {
          if ('forget' in dev && typeof dev.forget === 'function') {
            await dev.forget();
            forgotten++;
          }
        }
        return forgotten;
      } catch (e) {
        this.log(`Could not forget devices: ${(e as Error).message}`, 'warn');
        return 0;
      }
    }
    return 0;
  }

  public async scanAndConnect(forceScanAll?: boolean): Promise<DeviceTelemetry> {
    if (forceScanAll !== undefined) {
      this.scanAll = forceScanAll;
    }

    this.isUserDisconnecting = false;
    this.isRebootingSensor = false;

    if (this.mockMode) {
      this.log('Connecting to Virtual Simulated Sensor...', 'info');
      const telem = await this.mockDevice.connect();
      this.log(`Connected to Mock ${telem.deviceName} (${telem.model})`, 'success');
      return telem;
    }

    if (!this.isSupported()) {
      throw new Error(
        'Web Bluetooth is not supported in this browser. Please use Google Chrome, Microsoft Edge, or Bluefy (iOS).'
      );
    }

    const scanOptions: RequestDeviceOptions = this.scanAll
      ? {
          acceptAllDevices: true,
          optionalServices: [BLE_UUIDS.SERVICE, BLE_UUIDS.SERVICE_STR]
        }
      : {
          filters: [
            { namePrefix: 'FP102' },
            { namePrefix: 'FV801' },
            { namePrefix: 'FP' },
            { namePrefix: 'FV' }
          ],
          optionalServices: [BLE_UUIDS.SERVICE, BLE_UUIDS.SERVICE_STR]
        };

    this.log(
      this.scanAll
        ? 'Scanning for ALL nearby Bluetooth devices (filter disabled).'
        : 'Scanning with STRICT filter: exclusively FP102 & FV801 sensors (all unknown devices blocked)...',
      this.scanAll ? 'warn' : 'info'
    );

    this.isUserDisconnecting = false;
    this.isRebootingSensor = false;

    try {
      this.device = await navigator.bluetooth.requestDevice(scanOptions);

      this.device.addEventListener('gattserverdisconnected', () => {
        this.log('Bluetooth Device Disconnected', 'warn');
        if (this.onDisconnectListener) this.onDisconnectListener();
      });

      this.lastConnectedDevice = this.device;
      return await this.setupGattConnection();
    } catch (err: unknown) {
      this.log(`Connection failed: ${(err as Error).message}`, 'error');
      throw err;
    }
  }

  public async reconnect(): Promise<DeviceTelemetry> {
    this.isUserDisconnecting = false;
    this.isRebootingSensor = false;

    if (this.mockMode) {
      this.log('Reconnecting to Virtual Simulated Sensor...', 'info');
      const telem = await this.mockDevice.connect();
      this.isRebootingAfterFlash = false;
      this.log(`Reconnected to Mock ${telem.deviceName} (Version: ${telem.firmwareVersion})`, 'success');
      return telem;
    }

    if (!this.lastConnectedDevice) {
      return await this.scanAndConnect(false);
    }

    try {
      this.log(`Reconnecting to previously paired device (${this.lastConnectedDevice.name || 'Sensor'})...`, 'info');
      this.device = this.lastConnectedDevice;
      const telem = await this.setupGattConnection();
      this.isRebootingAfterFlash = false;
      this.log(`Reconnected successfully to ${telem.deviceName}!`, 'success');
      return telem;
    } catch {
      this.log('Quick reconnect failed (device may have changed MAC or timed out). Scanning again...', 'warn');
      return await this.scanAndConnect();
    }
  }

  private async setupGattConnection(): Promise<DeviceTelemetry> {
    if (!this.device || !this.device.gatt) throw new Error('No Bluetooth device available');

    this.log(`Connecting GATT to ${this.device.name || 'device'}...`, 'info');
    this.server = await this.device.gatt.connect();

    this.log('Discovering E104-BT52 Transparent Service (0xFFF0)...', 'info');
    const service = await this.server.getPrimaryService(BLE_UUIDS.SERVICE);

    this.log('Acquiring TX (0xFFF1) & RX (0xFFF2) characteristics...', 'info');
    this.txChar = await service.getCharacteristic(BLE_UUIDS.TX_NOTIFY);
    this.rxChar = await service.getCharacteristic(BLE_UUIDS.RX_WRITE);
    this.log(
      `RX Characteristic (0xFFF2) properties: write=${this.rxChar.properties.write}, writeWithoutResponse=${this.rxChar.properties.writeWithoutResponse}`,
      'info'
    );

    this.log('Subscribing to TX notifications...', 'info');
    await this.txChar.startNotifications();
    this.txChar.addEventListener('characteristicvaluechanged', (e: Event) => {
      const char = e.target as BluetoothRemoteGATTCharacteristic;
      if (!char.value) return;
      const bytes = new Uint8Array(char.value.buffer);
      const text = new TextDecoder().decode(bytes);
      this.handleIncomingChunk(bytes, text);
    });

    const name = this.device.name || '';
    const isMag = name.startsWith('FV801');
    const isPir = name.startsWith('FP102');
    const model = isMag ? 'FV801' : isPir ? 'FP102' : 'UNKNOWN';

    this.log(`Successfully connected to ${name}!`, 'success');

    // Query device telemetry immediately after establishing notifications
    setTimeout(() => {
      this.requestDeviceInfo().catch((err) => {
        this.log(`Auto telemetry query failed: ${(err as Error).message}`, 'warn');
      });
    }, 150);

    setTimeout(() => {
      this.requestCurrentData().catch(() => {});
    }, 400);

    return {
      model,
      deviceName: name,
      serialNumber: name.replace(/^(FP102|FV801)-?/, ''),
      firmwareVersion: 'Reading...',
      isConnected: true
    };
  }

  public async disconnect() {
    this.isUserDisconnecting = true;
    if (this.mockMode) {
      this.mockDevice.disconnect();
      if (this.onDisconnectListener) this.onDisconnectListener();
      return;
    }

    if (this.device && this.device.gatt && this.device.gatt.connected) {
      this.device.gatt.disconnect();
    }
    this.device = null;
    this.server = null;
    this.txChar = null;
    this.rxChar = null;
    this.rxBuffer = '';
    if (!this.isRebootingAfterFlash) {
      this.lastConnectedDevice = null;
    }
  }

  public isConnected(): boolean {
    if (this.mockMode) return this.mockDevice.isConnected;
    return !!(this.server && this.server.connected);
  }

  public async write(data: Uint8Array): Promise<void> {
    // Chain writes sequentially to prevent GATT operation in progress collision
    this.writeQueue = this.writeQueue
      .catch(() => {})
      .then(() => this.doWrite(data));
    return this.writeQueue;
  }

  private async doWrite(data: Uint8Array): Promise<void> {
    if (this.mockMode) {
      await this.mockDevice.write(data);
      return;
    }

    if (!this.rxChar) throw new Error('Bluetooth device not connected');
    
    const bufferSource = data as unknown as BufferSource;
    const rxChar = this.rxChar as BluetoothRemoteGATTCharacteristic & {
      writeValueWithResponse?(val: BufferSource): Promise<void>;
      writeValueWithoutResponse?(val: BufferSource): Promise<void>;
      writeValue?(val: BufferSource): Promise<void>;
    };

    if (this.rxChar.properties.write) {
      try {
        if (typeof rxChar.writeValueWithResponse === 'function') {
          await rxChar.writeValueWithResponse(bufferSource);
          return;
        } else if (typeof rxChar.writeValue === 'function') {
          await rxChar.writeValue(bufferSource);
          return;
        }
      } catch {
        // Fallback to writeWithoutResponse if writeWithResponse is unsupported
        if (this.rxChar.properties.writeWithoutResponse && typeof rxChar.writeValueWithoutResponse === 'function') {
          await rxChar.writeValueWithoutResponse(bufferSource);
          return;
        }
      }
    }

    if (this.rxChar.properties.writeWithoutResponse && typeof rxChar.writeValueWithoutResponse === 'function') {
      await rxChar.writeValueWithoutResponse(bufferSource);
    } else if (typeof rxChar.writeValue === 'function') {
      await rxChar.writeValue(bufferSource);
    }
  }

  public async sendJson(obj: Record<string, unknown>): Promise<void> {
    const jsonStr = JSON.stringify(obj) + '\r\n';
    this.log(`TX -> ${jsonStr.trim()}`, 'info');
    const bytes = new TextEncoder().encode(jsonStr);
    await this.write(bytes);
  }

  public async requestDeviceInfo(): Promise<void> {
    if (this.mockMode) {
      await this.mockDevice.write(new TextEncoder().encode('{"info":"get"}\r\n'));
      return;
    }
    this.log('Querying sensor configuration & telemetry (info: get)...', 'info');
    await this.sendJson({ info: 'get' });
  }

  public async configureServer(cfg: ServerConfig): Promise<void> {
    await this.sendJson({
      addr: cfg.addr,
      port: cfg.port,
      api: cfg.api
    });
  }

  public async configureApn(cfg: ApnConfig): Promise<void> {
    const payload: Record<string, string> = { apn: cfg.apn };
    if (cfg.apnN !== undefined) payload.apnN = cfg.apnN;
    if (cfg.apnP !== undefined) payload.apnP = cfg.apnP;
    await this.sendJson(payload);
  }

  public async requestCurrentData(): Promise<void> {
    if (this.mockMode) {
      await this.mockDevice.write(new TextEncoder().encode('{"currentData":"get"}\r\n'));
      return;
    }
    this.log('Querying footfall counters (currentData: get)...', 'info');
    await this.sendJson({ currentData: 'get' });
  }

  public async clearDayData(): Promise<void> {
    if (this.mockMode) {
      await this.mockDevice.write(new TextEncoder().encode('{"clearDay":"1"}\r\n'));
      return;
    }
    this.log('Wiping daily footfall counters (clearDay: 1)...', 'warn');
    await this.sendJson({ clearDay: '1' });
  }

  public async setMagCountInterval(intervalMs: number): Promise<void> {
    if (this.mockMode) {
      await this.mockDevice.write(new TextEncoder().encode(`{"countInterval":${intervalMs}}\r\n`));
      return;
    }
    this.log(`Writing FV801 magnetic count interval: ${intervalMs}ms...`, 'info');
    await this.sendJson({ countInterval: intervalMs });
  }

  public async rebootDevice(): Promise<void> {
    this.isRebootingSensor = true;
    if (this.mockMode) {
      await this.mockDevice.write(new TextEncoder().encode('{"restart":"1"}\r\n'));
      return;
    }
    this.log('Sending hardware reboot command (restart: 1)...', 'warn');
    await this.sendJson({ restart: '1' });
  }

  public async testCellularConnection(): Promise<void> {
    if (this.mockMode) {
      await this.mockDevice.write(new TextEncoder().encode('{"runLog":"on"}\r\n'));
      await this.mockDevice.write(new TextEncoder().encode('{"upload":"begin"}\r\n'));
      return;
    }
    this.log('Enabling live modem execution log (runLog: on)...', 'info');
    await this.sendJson({ runLog: 'on' });
    await new Promise((r) => setTimeout(r, 150));
    this.log('Triggering cellular network connect & upload (upload: begin)...', 'info');
    await this.sendJson({ upload: 'begin' });
  }

  public async stopRunLog(): Promise<void> {
    if (this.mockMode) {
      await this.mockDevice.write(new TextEncoder().encode('{"runLog":"off"}\r\n'));
      return;
    }
    this.log('Disabling live modem execution log (runLog: off)...', 'info');
    await this.sendJson({ runLog: 'off' });
  }
}
